import { DataAmigavel } from '@/shared/utils/datas';
import { resolveTipoGastoIcone } from '../tipo-gasto/tipo-gasto-icones';
import { Modal } from 'react-bootstrap';
import { ReadGastoDto } from '@/api.types';
import { LuChevronRight, LuList, LuPlus } from 'react-icons/lu';
import { useMemo, useState } from 'react';
import { ModalGastoForm } from './ModalGastoForm';
import { ReadGastoProjecaoDto } from './gasto-projecao.types';

interface GastoProjecaoCardProps {
  projecao: ReadGastoProjecaoDto;
  icone?: string | null;
  cor?: string | null;
  gastos: ReadGastoDto[];
  onGastosChanged: () => void;
}

function diasAteData(dataIso: string | null): number | null {
  if (!dataIso) return null;
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const alvo = new Date(dataIso);
  alvo.setHours(0, 0, 0, 0);
  return Math.round((alvo.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));
}

function formatarValor(valor: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
}

function iniciaisDoNome(nome: string) {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '—';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
}

export function GastoProjecaoCard({
  projecao,
  icone,
  cor,
  gastos,
  onGastosChanged,
}: GastoProjecaoCardProps) {
  const [modalAberto, setModalAberto] = useState(false);
  const [modalFormAberto, setModalFormAberto] = useState(false);
  const [gastoEditandoId, setGastoEditandoId] = useState(0);
  const titulo = projecao.tipoGastoDescricao || `Tipo #${projecao.tipoGastoId}`;
  const Icone = resolveTipoGastoIcone(icone);
  const corIcone = cor || '#0d6efd';
  const participantes = [...projecao.participantes].sort((a, b) => a.ordem - b.ordem);
  const diasParaProxima = diasAteData(projecao.proximaCompraEstimada);
  const resumoIntervalo =
    projecao.intervaloMedioDias != null ? `A cada ${projecao.intervaloMedioDias}d` : null;
  const nomeProximo = projecao.proximoQuemGastou?.nome?.trim() || null;
  const textoProximo =
    diasParaProxima != null && nomeProximo
      ? diasParaProxima < 0
        ? `Atrasado ${Math.abs(diasParaProxima)} dias (${nomeProximo})`
        : diasParaProxima === 0
          ? `Próximo hoje (${nomeProximo})`
          : `Próximo em ${diasParaProxima} dias (${nomeProximo})`
      : null;

  const compras = useMemo(
    () =>
      [...gastos]
        .filter((gasto) => gasto.tipoGasto?.id === projecao.tipoGastoId)
        .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()),
    [gastos, projecao.tipoGastoId]
  );

  const valorTotal = compras.reduce((sum, g) => sum + (Number(g.valor) || 0), 0);

  const rankingPagamentos = useMemo(() => {
    const porPessoa = new Map<string, { nome: string; iniciais: string; total: number }>();

    for (const gasto of compras) {
      const pessoa = gasto.quemGastou;
      if (!pessoa?.id) continue;
      const nome = pessoa.nome.trim();
      const atual = porPessoa.get(pessoa.id) ?? {
        nome,
        iniciais: iniciaisDoNome(nome),
        total: 0,
      };
      atual.total += Number(gasto.valor) || 0;
      porPessoa.set(pessoa.id, atual);
    }

    const lista = Array.from(porPessoa.values());
    if (lista.length === 0) {
      return { quemMaisPagou: null, quemMenosPagou: null };
    }

    let quemMaisPagou = lista[0];
    let quemMenosPagou = lista[0];
    for (const item of lista) {
      if (item.total > quemMaisPagou.total) quemMaisPagou = item;
      if (item.total < quemMenosPagou.total) quemMenosPagou = item;
    }

    return { quemMaisPagou, quemMenosPagou };
  }, [compras]);

  return (
    <>
      <div className="card h-100 shadow-sm border-0 gasto-projecao-card">
        <div className="card-body d-flex flex-column">
          <div className={`d-flex justify-content-between align-items-center gap-2 ${textoProximo ? 'mb-1' : 'mb-2'}`}>
            <h6 className="card-title mb-0 d-flex align-items-center gap-2">
              {Icone && (
                <Icone size={18} className="flex-shrink-0" aria-hidden style={{ color: corIcone }} />
              )}
              {titulo}
            </h6>
            <div className="d-flex align-items-center gap-2">
              {resumoIntervalo && (
                <span className="text-muted small text-nowrap">{resumoIntervalo}</span>
              )}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm py-0 px-1"
                title="Ver compras"
                aria-label={`Ver compras de ${titulo}`}
                onClick={() => setModalAberto(true)}
              >
                <LuList size={14} aria-hidden />
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm py-0 px-1"
                title="Cadastrar gasto"
                aria-label={`Cadastrar gasto de ${titulo}`}
                onClick={() => {
                  setGastoEditandoId(0);
                  setModalFormAberto(true);
                }}
              >
                <LuPlus size={14} aria-hidden />
              </button>
            </div>
          </div>

          {textoProximo && (
            <p className="text-muted small mb-2">{textoProximo}</p>
          )}

          {participantes.length > 0 && (
            <div>
              <div className="d-flex flex-wrap align-items-center gap-1">
                {participantes.map((participante, index) => {
                  const isProximo = projecao.proximoQuemGastou?.id === participante.id;
                  const isUltimo = projecao.ultimoQuemGastou?.id === participante.id;
                  const setaParaProximo =
                    index < participantes.length - 1 &&
                    projecao.proximoQuemGastou?.id === participantes[index + 1].id;

                  let titlePill = participante.nome.trim();
                  if (isProximo) {
                    const partes = [participante.nome.trim()];
                    if (projecao.proximaCompraEstimada) {
                      partes.push(DataAmigavel(projecao.proximaCompraEstimada));
                    }
                    if (diasParaProxima != null) {
                      partes.push(
                        diasParaProxima < 0
                          ? `atrasado ${Math.abs(diasParaProxima)} dias`
                          : `em ${diasParaProxima} dias`
                      );
                    }
                    titlePill = partes.join(' - ');
                  } else if (isUltimo) {
                    const partes = [participante.nome.trim()];
                    if (projecao.ultimaCompra) {
                      partes.push(DataAmigavel(projecao.ultimaCompra));
                    }
                    if (projecao.diasDesdeUltimaCompra != null) {
                      partes.push(`há ${projecao.diasDesdeUltimaCompra} dias`);
                    }
                    titlePill = partes.join(' - ');
                  }

                  return (
                    <div key={participante.id} className="d-flex align-items-center gap-1">
                      <span
                        className={`badge rounded-pill ${
                          isProximo
                            ? 'bg-primary'
                            : isUltimo
                              ? 'bg-secondary'
                              : 'bg-light text-dark border'
                        }`}
                        title={titlePill}
                      >
                        {participante.iniciais}
                      </span>
                      {index < participantes.length - 1 && (
                        <LuChevronRight
                          size={14}
                          className={setaParaProximo ? 'text-primary' : 'text-muted'}
                          aria-hidden
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal show={modalAberto} onHide={() => setModalAberto(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            {Icone && <Icone size={20} aria-hidden style={{ color: corIcone }} />}
            Compras — {titulo}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {compras.length === 0 ? (
            <p className="text-muted mb-0">Nenhuma compra registrada.</p>
          ) : (
            <>
              <div className="mb-3 d-flex flex-wrap gap-3">
                <span>
                  <strong>Total:</strong> {compras.length}
                </span>
                <span>
                  <strong>Valor:</strong> {formatarValor(valorTotal)}
                </span>
                {rankingPagamentos.quemMaisPagou && (
                  <span title={`${rankingPagamentos.quemMaisPagou.nome} (${formatarValor(rankingPagamentos.quemMaisPagou.total)})`}>
                    <strong>Mais:</strong> {rankingPagamentos.quemMaisPagou.iniciais}
                  </span>
                )}
                {rankingPagamentos.quemMenosPagou && (
                  <span title={`${rankingPagamentos.quemMenosPagou.nome} (${formatarValor(rankingPagamentos.quemMenosPagou.total)})`}>
                    <strong>Menos:</strong> {rankingPagamentos.quemMenosPagou.iniciais}
                  </span>
                )}
              </div>
              <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <table className="table table-striped table-hover mb-0">
                  <thead className="sticky-top bg-white">
                    <tr>
                      <th>Data</th>
                      <th>Quem Gastou</th>
                      <th>Valor</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compras.map((gasto) => (
                      <tr key={gasto.id}>
                        <td>{gasto.data ? DataAmigavel(gasto.data) : ''}</td>
                        <td>{gasto.quemGastou ? gasto.quemGastou.nome.trim() : ''}</td>
                        <td>{formatarValor(Number(gasto.valor) || 0)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-link link-secondary p-0"
                            onClick={() => {
                              setGastoEditandoId(gasto.id);
                              setModalFormAberto(true);
                            }}
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={() => setModalAberto(false)}>
            Fechar
          </button>
        </Modal.Footer>
      </Modal>

      <ModalGastoForm
        show={modalFormAberto}
        onHide={() => setModalFormAberto(false)}
        onSaved={onGastosChanged}
        gastoId={gastoEditandoId}
        tipoGastoId={gastoEditandoId > 0 ? 0 : projecao.tipoGastoId}
      />
    </>
  );
}
